import { useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track, type RemoteParticipant } from "livekit-client";

export interface ParticipantStatus {
  identity: string;
  name: string;
  micAllowed: boolean;
  camAllowed: boolean;
  handRaised: boolean;
  connected: boolean;
}

interface ParticipantsPanelProps {
  isHost: boolean;
  hostIdentity: string;
  participants: ParticipantStatus[];
  onGrantMic: (identity: string, allow: boolean) => void;
  onGrantCam: (identity: string, allow: boolean) => void;
  onLowerHand: (identity: string) => void;
  onClose: () => void;
}

function StatusDot({ active, color }: { active: boolean; color: string }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${active ? color : "bg-background-600"}`}
    />
  );
}

export default function ParticipantsPanel({
  isHost,
  hostIdentity,
  participants,
  onGrantMic,
  onGrantCam,
  onLowerHand,
  onClose,
}: ParticipantsPanelProps) {
  const room = useRoomContext();
  const [mutedMap, setMutedMap] = useState<Record<string, { mic: boolean; cam: boolean }>>({});

  useEffect(() => {
    const update = () => {
      const next: Record<string, { mic: boolean; cam: boolean }> = {};
      room.remoteParticipants.forEach((p: RemoteParticipant) => {
        const micPub = p.getTrackPublication(Track.Source.Microphone);
        const camPub = p.getTrackPublication(Track.Source.Camera);
        next[p.identity] = {
          mic: micPub ? !micPub.isMuted : false,
          cam: camPub ? !camPub.isMuted : false,
        };
      });
      setMutedMap(next);
    };
    update();
    room.on(RoomEvent.TrackMuted, update);
    room.on(RoomEvent.TrackUnmuted, update);
    room.on(RoomEvent.ParticipantConnected, update);
    room.on(RoomEvent.ParticipantDisconnected, update);
    return () => {
      room.off(RoomEvent.TrackMuted, update);
      room.off(RoomEvent.TrackUnmuted, update);
      room.off(RoomEvent.ParticipantConnected, update);
      room.off(RoomEvent.ParticipantDisconnected, update);
    };
  }, [room]);

  const students = participants.filter((p) => p.identity !== hostIdentity);
  const raisedCount = students.filter((p) => p.handRaised).length;

  return (
    <div className="w-72 shrink-0 border-l border-background-700/50 bg-background-900 flex flex-col">
      {/* Header */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-background-700/50 shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-background-100">Katılımcılar</h3>
          <span className="px-1.5 py-0.5 rounded bg-background-700 text-background-300 text-xs font-medium">
            {students.length}
          </span>
          {raisedCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-accent-500 text-white text-xs font-bold animate-pulse">
              {raisedCount} el
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-md text-background-300 hover:bg-background-700 transition-colors"
          aria-label="Paneli kapat"
        >
          <i className="ri-close-line" aria-hidden="true"></i>
        </button>
      </div>

      {/* Legend (host only) */}
      {isHost && (
        <div className="px-4 pt-3 pb-2 border-b border-background-700/30 shrink-0">
          <p className="text-[11px] text-background-500 font-medium uppercase tracking-wide mb-1.5">Öğretmen kontrolleri</p>
          <div className="flex flex-wrap gap-2 text-[11px] text-background-400">
            <span className="flex items-center gap-1">
              <i className="ri-mic-line text-xs text-accent-400"></i> Mikrofon ver/al
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-vidicon-line text-xs text-primary-400"></i> Kamera ver/al
            </span>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto py-2">
        {students.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-800 text-background-500">
              <i className="ri-user-line text-xl" aria-hidden="true"></i>
            </span>
            <p className="text-xs text-background-500">Henüz öğrenci bağlanmadı</p>
          </div>
        ) : (
          students.map((p) => {
            const live = mutedMap[p.identity];
            const micActive = live?.mic ?? false;
            const camActive = live?.cam ?? false;

            return (
              <div
                key={p.identity}
                className={`mx-2 my-1 rounded-lg px-3 py-2.5 transition-colors ${
                  p.handRaised ? "bg-accent-500/15 border border-accent-500/30" : "bg-background-800/60 hover:bg-background-800"
                }`}
              >
                {/* Name + hand badge */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative w-7 h-7 flex items-center justify-center rounded-full bg-background-700 shrink-0">
                    <span className="text-xs font-bold text-background-200">
                      {(p.name || "?")[0].toUpperCase()}
                    </span>
                    <StatusDot active={p.connected} color="bg-green-400" />
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border border-background-800 hidden data-[connected=true]:block" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-background-100 truncate">{p.name}</p>
                  </div>
                  {p.handRaised && (
                    <span className="text-accent-400 text-sm shrink-0" title="El kaldırdı">
                      <i className="fa-solid fa-hand" aria-hidden="true"></i>
                    </span>
                  )}
                </div>

                {/* Track status row */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      micActive ? "bg-green-500/20 text-green-400" : "bg-background-700 text-background-500"
                    }`}
                  >
                    <i className={micActive ? "ri-mic-line" : "ri-mic-off-line"} aria-hidden="true"></i>
                    {micActive ? "Açık" : "Kapalı"}
                  </span>
                  <span
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      camActive ? "bg-green-500/20 text-green-400" : "bg-background-700 text-background-500"
                    }`}
                  >
                    <i className={camActive ? "ri-vidicon-line" : "ri-vidicon-2-line"} aria-hidden="true"></i>
                    {camActive ? "Açık" : "Kapalı"}
                  </span>
                </div>

                {/* Host controls */}
                {isHost && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => onGrantMic(p.identity, !p.micAllowed)}
                      className={`flex-1 h-7 rounded flex items-center justify-center gap-1 text-[11px] font-semibold transition-colors whitespace-nowrap ${
                        p.micAllowed
                          ? "bg-green-600/25 text-green-400 hover:bg-green-600/40"
                          : "bg-background-700 text-background-400 hover:bg-background-600"
                      }`}
                      title={p.micAllowed ? "Mikrofonu kapat" : "Mikrofon ver"}
                    >
                      <i className={`text-xs ${p.micAllowed ? "ri-mic-line" : "ri-mic-fill"}`} aria-hidden="true"></i>
                      {p.micAllowed ? "Mik. Var" : "Mik. Yok"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onGrantCam(p.identity, !p.camAllowed)}
                      className={`flex-1 h-7 rounded flex items-center justify-center gap-1 text-[11px] font-semibold transition-colors whitespace-nowrap ${
                        p.camAllowed
                          ? "bg-primary-600/25 text-primary-400 hover:bg-primary-600/40"
                          : "bg-background-700 text-background-400 hover:bg-background-600"
                      }`}
                      title={p.camAllowed ? "Kamerayı kapat" : "Kamera ver"}
                    >
                      <i className={`text-xs ${p.camAllowed ? "ri-vidicon-line" : "ri-vidicon-2-line"}`} aria-hidden="true"></i>
                      {p.camAllowed ? "Kam. Var" : "Kam. Yok"}
                    </button>
                    {p.handRaised && (
                      <button
                        type="button"
                        onClick={() => onLowerHand(p.identity)}
                        className="h-7 px-2 rounded bg-accent-600/25 text-accent-400 hover:bg-accent-600/40 text-[11px] font-semibold transition-colors whitespace-nowrap"
                        title="Eli indir"
                      >
                        <i className="ri-arrow-down-s-line text-xs" aria-hidden="true"></i> İndir
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}